import React, { useState } from 'react'
import { 
  EnvelopeIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  LinkIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { 
  useGetInvitationsQuery, 
  useGetInvitationStatsQuery, 
  useDeleteInvitationMutation,
  useResendInvitationMutation,
  InvitationFilters 
} from '../store/api/invitationsApi'
import SortableTableHeader from '../components/SortableTableHeader'
import { formatDateTimeEST } from '../utils/dateUtils'

const Invitations: React.FC = () => {
  const [filters, setFilters] = useState<InvitationFilters>({
    page: 1,
    limit: 20,
    search: '',
    status: 'all',
    roleType: 'all',
    sortBy: 'created_at',
    sortOrder: 'DESC'
  });

  const { data: invitationsData, isLoading, error } = useGetInvitationsQuery(filters)
  const { data: statsData, isLoading: statsLoading } = useGetInvitationStatsQuery()
  const [deleteInvitation] = useDeleteInvitationMutation()
  const [resendInvitation] = useResendInvitationMutation()
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [resendingId, setResendingId] = useState<number | null>(null)

  // Handle search
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof InvitationFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  // Handle sorting
  const handleSort = (sortBy: string, sortOrder: 'ASC' | 'DESC') => {
    setFilters(prev => ({ ...prev, sortBy, sortOrder, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle copy to clipboard
  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Invitation link copied to clipboard!');
  };

  const handleCopyEmailLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('Email invitation link copied to clipboard!');
  };

  // Handle delete invitation
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this invitation?')) {
      return;
    }

    try {
      setDeletingId(id);
      await deleteInvitation(id).unwrap();
      toast.success('Invitation deleted successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete invitation');
    } finally {
      setDeletingId(null);
    }
  };

  // Handle resend invitation
  const handleResend = async (id: number) => {
    try {
      setResendingId(id);
      await resendInvitation(id).unwrap();
      toast.success('Invitation marked for resend');
    } catch (err) {
      console.error(err);
      toast.error('Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const invitations = invitationsData?.data || []
  const stats = statsData?.data || {
    totalInvitations: 0,
    pendingInvitations: 0,
    acceptedInvitations: 0,
    participantInvitations: 0,
    teamMemberInvitations: 0,
    guestInvitations: 0
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Error loading invitations. Please try again.</p>
      </div>
    )
  }

  const getStatusColor = (accepted: number) => {
    if (accepted === 1) return 'bg-green-100 text-green-800'
    if (accepted === -1) return 'bg-red-100 text-red-800'
    return 'bg-yellow-100 text-yellow-800'
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'PARTICIPANT': return 'bg-purple-100 text-purple-800'
      case 'TEAM_LEADER': return 'bg-blue-100 text-blue-800'
      case 'TEAM_MEMBER': return 'bg-cyan-100 text-cyan-800'
      case 'GUEST': return 'bg-yellow-100 text-yellow-800'
      case 'MO_CHECK_COLLABORATOR': return 'bg-emerald-100 text-emerald-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  /** Admin list label — Mo Check invites must read as Collaborator, not TEAM_MEMBER from joined roles */
  const formatRoleLabel = (role: string | undefined) => {
    if (!role) return 'N/A'
    if (role === 'MO_CHECK_COLLABORATOR') return 'Collaborator'
    return role.replace(/_/g, ' ')
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invitation Links</h1>
          <p className="text-gray-600">
            View invitation links. Copy the invite link (canonical) or the envelope icon for the exact URL sent in emails (useful on localhost when emails are not delivered).
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.totalInvitations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.pendingInvitations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Accepted</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.acceptedInvitations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-purple-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Participants</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.participantInvitations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-blue-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Team</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.teamMemberInvitations}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-6 h-6 bg-yellow-500 rounded-full"></div>
            </div>
            <div className="ml-3">
              <p className="text-xs text-gray-500">Guests</p>
              <p className="text-xl font-bold text-gray-900">{statsLoading ? '-' : stats.guestInvitations}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search invitations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.roleType}
              onChange={(e) => handleFilterChange('roleType', e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="PARTICIPANT">Participants</option>
              <option value="TEAM_LEADER">Team Leaders</option>
              <option value="TEAM_MEMBER">Team Members</option>
              <option value="GUEST">Guests</option>
              <option value="MO_CHECK_COLLABORATOR">Mo collaborators</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invitations Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableTableHeader
                  label="Email"
                  sortKey="email"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Role"
                  sortKey="role_name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Project"
                  sortKey="project_name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Organization"
                  sortKey="organization_name"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invited By
                </th>
                <SortableTableHeader
                  label="Status"
                  sortKey="accepted"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <SortableTableHeader
                  label="Created"
                  sortKey="created_at"
                  currentSortBy={filters.sortBy}
                  currentSortOrder={filters.sortOrder}
                  onSort={handleSort}
                />
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invitations.map((invitation) => (
                <tr key={invitation.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(invitation.role_name)}`}>
                      {formatRoleLabel(invitation.role_name)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invitation.project_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {invitation.organization_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{invitation.invited_by_name}</div>
                    <div className="text-xs text-gray-500">{invitation.invited_by_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(invitation.accepted)}`}>
                      {invitation.accepted === 1 ? (
                        <>
                          <CheckCircleIcon className="h-3 w-3" />
                          Accepted
                        </>
                      ) : invitation.accepted === -1 ? (
                        <>
                          <span className="h-3 w-3 rounded-full bg-red-600"></span>
                          Rejected
                        </>
                      ) : (
                        <>
                          <ClockIcon className="h-3 w-3" />
                          Pending
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDateTimeEST(invitation.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopyLink(invitation.invitation_url)}
                        className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        title="Copy invite link"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCopyEmailLink(invitation.email_invitation_url)}
                        className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                        title="Copy email link (exact URL sent in invitation email)"
                      >
                        <EnvelopeIcon className="h-4 w-4" />
                      </button>
                      <a
                        href={invitation.invitation_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 hover:text-green-800"
                        title="Open Link"
                      >
                        <LinkIcon className="h-4 w-4" />
                      </a>
                      {invitation.accepted === 0 && (
                        <button
                          onClick={() => handleResend(invitation.id)}
                          disabled={resendingId === invitation.id}
                          className="text-purple-600 hover:text-purple-800 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Resend"
                        >
                          <PaperAirplaneIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(invitation.id)}
                        disabled={deletingId === invitation.id}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {invitationsData?.meta && invitationsData.meta.totalPages > 1 && (
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(1, (invitationsData.meta?.page || 1) - 1))}
                disabled={invitationsData.meta.page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(Math.min(invitationsData.meta.totalPages, (invitationsData.meta?.page || 1) + 1))}
                disabled={invitationsData.meta.page === invitationsData.meta.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {((invitationsData.meta.page - 1) * invitationsData.meta.limit) + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(invitationsData.meta.page * invitationsData.meta.limit, invitationsData.meta.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium">{invitationsData.meta.total}</span>{' '}
                  invitations
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(Math.max(1, invitationsData.meta.page - 1))}
                    disabled={invitationsData.meta.page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {[...Array(Math.min(5, invitationsData.meta.totalPages))].map((_, index) => {
                    const pageStart = Math.max(1, Math.min(invitationsData.meta.totalPages - 4, invitationsData.meta.page - 2));
                    const pageNum = pageStart + index;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pageNum === invitationsData.meta.page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(Math.min(invitationsData.meta.totalPages, invitationsData.meta.page + 1))}
                    disabled={invitationsData.meta.page === invitationsData.meta.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
          )}
      </div>
    </div>
  )
}

export default Invitations

